import {
  HelpTip,
  ManageDocumentDialog as SharedManageDocumentDialog,
  dateFieldSuggestions,
  type ManageDocumentHooks,
  type ManageDocumentSuggestions,
} from '@dataroom/shared';
import type { RoomDocument } from '@dataroom/shared';
import {
  useApproveProposal,
  useModifyDocumentAttributes,
  usePendingProposals,
  useRejectProposal,
  useRoomCategories,
  useSetDocumentDating,
} from '@/api/hooks';

interface ManageDocumentDialogProps {
  roomId: string;
  /** The LIVE document (the caller re-reads it from the polled listing), so the
   * "Currently" summary shows each change once it lands. */
  document: RoomDocument;
  onClose: () => void;
  /** "Put it in more than one folder…": opens the category-tags dialog. */
  onEditTags?: () => void;
}

/** The AI's pending period / date suggestions for one document, with Confirm and Reject. */
function useAdminSuggestions(roomId: string, documentId: string): ManageDocumentSuggestions {
  const pending = usePendingProposals(roomId);
  const approve = useApproveProposal(roomId);
  const reject = useRejectProposal(roomId);
  return {
    suggestions: dateFieldSuggestions(documentId, pending.data ?? []),
    loading: pending.isPending,
    approve,
    reject,
  };
}

/** The room admin's hooks for the shared dialog (module-level: stable identities). */
const ADMIN_HOOKS: ManageDocumentHooks = {
  useCategories: useRoomCategories,
  useFile: useModifyDocumentAttributes,
  useDating: useSetDocumentDating,
  useSuggestions: useAdminSuggestions,
};

/**
 * "Manage document" (owner ask 2026-10-15) — the shared dialog
 * (shared/src/ui/manage-document-dialog.tsx; the seller portal opens the same
 * one) with the room admin's data hooks, including the AI's pending date
 * suggestions to Confirm or Reject.
 */
export function ManageDocumentDialog({
  roomId,
  document,
  onClose,
  onEditTags,
}: ManageDocumentDialogProps) {
  return (
    <SharedManageDocumentDialog
      roomId={roomId}
      document={document}
      onClose={onClose}
      hooks={ADMIN_HOOKS}
      onEditTags={onEditTags}
      dateHeadingAside={<HelpTip id="admin.manage-document.period" />}
    />
  );
}
